import { calculateElementBounds } from './elementBoundsCalculator'
import type { CanvasElement } from '../entities/element'

/**
 * Bleed Coverage Service
 * Detects photos whose edge ends inside the bleed band: they cross the trim line
 * (design clipPath) but stop before the bleed contour (export clipPath), which
 * would leave insufficient bleed after cutting.
 */

type Zone = { left: number; top: number; width: number; height: number; clipPath?: string }

type Zones = {
  export: Zone
  design: Zone
}

const SVG_NS = 'http://www.w3.org/2000/svg'
const EDGE_SAMPLES = 12

const rectToPath = (zone: Zone): string =>
  `M ${zone.left},${zone.top} h ${zone.width} v ${zone.height} h ${-zone.width} Z`

const createPath = (d: string): SVGPathElement => {
  const path = document.createElementNS(SVG_NS, 'path')
  path.setAttribute('d', d)
  return path
}

// Points along the outline of the element's (rotated) bounding box
const sampleOutlinePoints = (
  bounds: { x: number; y: number; width: number; height: number },
  rotationDegrees: number,
): Array<{ x: number; y: number }> => {
  const cx = bounds.x + bounds.width / 2
  const cy = bounds.y + bounds.height / 2
  const angle = (rotationDegrees * Math.PI) / 180
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)

  const steps = Array.from({ length: EDGE_SAMPLES + 1 }, (_, i) => i / EDGE_SAMPLES)
  const edges = [
    (t: number) => ({ x: bounds.x + t * bounds.width, y: bounds.y }),
    (t: number) => ({ x: bounds.x + bounds.width, y: bounds.y + t * bounds.height }),
    (t: number) => ({ x: bounds.x + t * bounds.width, y: bounds.y + bounds.height }),
    (t: number) => ({ x: bounds.x, y: bounds.y + t * bounds.height }),
  ]

  return edges.flatMap((edge) =>
    steps.map((t) => {
      const { x, y } = edge(t)
      return {
        x: cx + (x - cx) * cos - (y - cy) * sin,
        y: cy + (x - cx) * sin + (y - cy) * cos,
      }
    }),
  )
}

export const findElementsWithInsufficientBleed = (
  elements: CanvasElement[],
  zones: Zones,
): string[] => {
  const bleedD = zones.export.clipPath
  if (!bleedD) return []

  const trimD = zones.design.clipPath || rectToPath(zones.design)

  // isPointInFill needs rendered geometry in some browsers, so the paths are
  // tested inside a hidden svg attached to the document
  const svg = document.createElementNS(SVG_NS, 'svg')
  svg.setAttribute('width', '0')
  svg.setAttribute('height', '0')
  svg.style.position = 'absolute'
  svg.style.visibility = 'hidden'
  const bleedPath = createPath(bleedD)
  const trimPath = createPath(trimD)
  svg.append(bleedPath, trimPath)
  document.body.appendChild(svg)

  const point = svg.createSVGPoint()
  const isInBleedBand = ({ x, y }: { x: number; y: number }): boolean => {
    point.x = x
    point.y = y
    return bleedPath.isPointInFill(point) && !trimPath.isPointInFill(point)
  }

  try {
    return elements
      .filter((element) => element.type === 'image')
      .filter((element) => {
        const bounds = calculateElementBounds(element, zones.export)
        const outline = sampleOutlinePoints(bounds, element.transform?.rotation || 0)
        return outline.some(isInBleedBand)
      })
      .map((element) => element.id)
  } finally {
    svg.remove()
  }
}
